import { RefObject, useCallback, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import { MDSSnackbar } from '@marqvision/mds-v2';
import { usePutFile } from './usePutFile';

const MAX_FILE_SIZE = 20971520;

type Props = {
  quill: RefObject<ReactQuill | null>;
  onGetUploadUrl?: (filename: string) => Promise<string>;
  onGetPresignedUrl?: (url: string) => Promise<string>;
};

export const useViewModel = (props: Props) => {
  const { quill, onGetUploadUrl, onGetPresignedUrl } = props;

  const [selectedLinkLabel, setSelectedLinkLabel] = useState<{
    index: number;
    length: number;
    label: string;
  }>();
  const [isUploading, setIsUpLoading] = useState(false);

  const onGetUploadUrlRef = useRef(onGetUploadUrl);
  const onGetPresignedUrlRef = useRef(onGetPresignedUrl);

  const { mutateAsync: putFileMutate } = usePutFile();

  const handleClickLinkToolbar = useCallback(() => {
    const editor = quill.current?.getEditor();

    if (editor) {
      const range = editor.getSelection();
      if (range) {
        setSelectedLinkLabel({
          ...range,
          label: range.length > 0 ? editor.getText(range.index, range.length) : '',
        });
      } else {
        setSelectedLinkLabel(undefined);
      }
    }
  }, [quill]);

  const handleInsertLink = (value: { label: string; url: string }) => {
    const editor = quill.current?.getEditor();
    if (editor) {
      if (selectedLinkLabel) {
        if (selectedLinkLabel.length > 0) {
          editor.deleteText(selectedLinkLabel.index, selectedLinkLabel.length, 'user');
        }
        editor.insertText(selectedLinkLabel.index, value.label, 'link', value.url, 'user');
      } else {
        editor.insertText(editor.getLength(), value.label, 'link', value.url, 'user');
      }
      editor.setSelection(
        selectedLinkLabel ? selectedLinkLabel.index + selectedLinkLabel.length : editor.getLength(),
        0
      );
    }
  };

  const handleInsertImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        MDSSnackbar({
          type: 'error',
          title: `error`,
          message: 'Maximum size is 20MB',
        });
        return;
      }
      setIsUpLoading(true);
      const uploadUrl = await onGetUploadUrlRef.current?.(file.name);
      if (uploadUrl) {
        await putFileMutate({
          url: uploadUrl,
          file,
        });

        const url = await (async () => {
          const rootUrl = uploadUrl.split('?')[0];
          if (onGetPresignedUrlRef.current) {
            return await onGetPresignedUrlRef.current(rootUrl);
          }
          return rootUrl;
        })();

        const editor = quill.current?.getEditor();

        if (editor) {
          if (!quill.current?.value) {
            editor.insertText(0, '');
          }
          setTimeout(() => {
            const selection = editor.getSelection();
            editor.insertEmbed(selection ? selection.index : editor.getLength(), 'image', url, 'user');
            editor.setSelection(editor.getLength(), 0);
            setIsUpLoading(false);
          }, 0);
        }
      }
    };
  }, [quill, putFileMutate]);

  return {
    selectedLinkLabel: selectedLinkLabel?.label,
    isUploading,
    onClickLinkToolbar: handleClickLinkToolbar,
    onInsertLink: handleInsertLink,
    onInsertImage: handleInsertImage,
  };
};
